import type {
    AclConfigHelpers,
    ReadableSubjectKey,
    SubjectFieldKey,
    SubjectPayload,
} from '@/types/acl-types';

type AllExtraFieldsMap = {
    [K in ReadableSubjectKey]?: Array<keyof SubjectPayload<K>>;
};

// Add here all fields that should be hidden by default
const extraFieldsMap = {
    Stats: ['total_margin'],
    ProductQuoteOrder: ['product_gross_margin_in_order'],
    CustomerFinancial: [
        'gross_margin_ttm',
        'gross_margin_ttm_percentage',
        'gross_margin_ytd_current',
        'gross_margin_ytd_percentage',
    ],
    QuoteOrder: ['released_date', 'total_margin'],
    QuoteOrderLineItem: ['encrypted_purchase_price', 'discount_percent', 'gross_margin'],
    SalesDraftLineItem: ['rebate', 'gross_margin', 'external.encoded_price', 'encrypted_purchase_price'],
    SalesDraft: ['storage_id'],
} satisfies AllExtraFieldsMap;

export type SubjectKeyWithExtraFields = keyof typeof extraFieldsMap;

export type ExtraFields<K extends SubjectKeyWithExtraFields> = (typeof extraFieldsMap)[K];
export type ExtraFieldsConfig<K extends SubjectKeyWithExtraFields> = Record<ExtraFields<K>[number], boolean>;

export function allowRead<TSubject extends SubjectKeyWithExtraFields>(
    { allow, restrict }: AclConfigHelpers,
    subject: TSubject,
    extraFieldsConfig: ExtraFieldsConfig<TSubject>,
) {
    allow('read', subject);

    const extraFields = extraFieldsMap[subject] ?? [];

    for (const field of extraFields) {
        const isExtraFieldVisible = Boolean((extraFieldsConfig as Record<string, boolean>)?.[field]);

        if (!isExtraFieldVisible) {
            restrict('read', subject, [field as SubjectFieldKey<TSubject>]);
        }
    }
}
